import { AccountGroupService } from '../account-group';
import { FeatureFlagService } from '../core/feature-flag.service';
import { TranslateService } from '@ngx-translate/core';
import {
  AddToListMultiRowAction,
  AddToListSingleRowAction,
  AddToListSelectAllAction,
  ListActionsService,
} from '@galaxy/crm/integrations/dynamic-lists-actions';
import { combineLatest, map, Observable, switchMap } from 'rxjs';
import { MultiRowAction, SingleRowAction } from '@galaxy/crm/static';
import { DynamicListsFeature } from './constants';
import { ExportActionsService, ExportSelectAllRowAction } from '@galaxy/crm/dynamic';
import { CRMAccessService } from './access.service';
import {
  AutomationActionsService,
  AutomationMultiRowAction,
  AutomationSelectAllRowAction,
  AutomationSingleRowAction,
} from '@galaxy/crm/integrations/automation';

export function buildCompanySingleRowTableActions(
  accountGroupService: AccountGroupService,
  featureFlagService: FeatureFlagService,
  listActionsService: ListActionsService,
  accessService: CRMAccessService,
  automationActionService: AutomationActionsService,
): Observable<SingleRowAction[]> {
  return accountGroupService.currentAccountGroup$.pipe(
    switchMap((currentAccountGroup) => {
      return combineLatest([
        accountGroupService.currentAccountGroupId$,
        featureFlagService.checkFeatureFlag(currentAccountGroup.partnerId, '', DynamicListsFeature),
        accessService.canRunManualAutomations$,
      ]);
    }),
    map(([accountGroupId, canAddToList, canRunAutomations]) => {
      const actions: SingleRowAction[] = [];
      if (canAddToList) {
        actions.push(AddToListSingleRowAction(accountGroupId, listActionsService, 'Company', 'CRM.ADD_TO_STATIC_LIST'));
      }
      if (canRunAutomations) {
        actions.push(
          AutomationSingleRowAction(
            accountGroupId,
            automationActionService,
            'Company',
            'COMPANY.LIST_OBJECTS_TABLE.ACTIONS.START_AUTOMATION',
          ),
        );
      }
      return actions;
    }),
  );
}

export function buildCompanyMultiRowTableActions(
  accountGroupService: AccountGroupService,
  featureFlagService: FeatureFlagService,
  translationSvc: TranslateService,
  listActionsService: ListActionsService,
  accessService: CRMAccessService,
  automationActionService: AutomationActionsService,
): Observable<MultiRowAction[]> {
  return accountGroupService.currentAccountGroup$.pipe(
    switchMap((currentAccountGroup) => {
      return combineLatest([
        accountGroupService.currentAccountGroupId$,
        featureFlagService.checkFeatureFlag(currentAccountGroup.partnerId, '', DynamicListsFeature),
        accessService.canRunManualAutomations$,
      ]);
    }),
    map(([accountGroupId, canAddToList, canRunAutomations]) => {
      const actions: MultiRowAction[] = [];
      if (canAddToList) {
        actions.push(
          AddToListMultiRowAction(
            accountGroupId,
            listActionsService,
            'Company',
            translationSvc.instant('CRM.ADD_TO_STATIC_LIST'),
          ),
        );
      }
      if (canRunAutomations) {
        actions.push(
          AutomationMultiRowAction(
            accountGroupId,
            automationActionService,
            'Company',
            'COMPANY.LIST_OBJECTS_TABLE.ACTIONS.START_AUTOMATION',
          ),
        );
      }
      return actions;
    }),
  );
}

export function buildCompanySelectAllTableActions(
  accountGroupService: AccountGroupService,
  featureFlagService: FeatureFlagService,
  translationSvc: TranslateService,
  listActionsService: ListActionsService,
  exportActionsService: ExportActionsService,
  accessService: CRMAccessService,
  automationActionService: AutomationActionsService,
): Observable<MultiRowAction[]> {
  return accountGroupService.currentAccountGroup$.pipe(
    switchMap((currentAccountGroup) => {
      return combineLatest([
        accountGroupService.currentAccountGroupId$,
        featureFlagService.checkFeatureFlag(currentAccountGroup.partnerId, '', DynamicListsFeature),
        accessService.canRunManualAutomations$,
      ]);
    }),
    map(([accountGroupId, canAddToList, canRunAutomations]) => {
      const actions: MultiRowAction[] = [];
      if (canAddToList) {
        actions.push(
          AddToListSelectAllAction(
            accountGroupId,
            listActionsService,
            'Company',
            translationSvc.instant('CRM.ADD_TO_STATIC_LIST'),
          ),
        );
      }
      if (canRunAutomations) {
        actions.push(
          AutomationSelectAllRowAction(
            accountGroupId,
            automationActionService,
            'Company',
            'COMPANY.LIST_OBJECTS_TABLE.ACTIONS.START_AUTOMATION',
          ),
        );
      }
      actions.push(
        ExportSelectAllRowAction(accountGroupId, exportActionsService, 'Company', 'ACTIONS.CRM.EXPORT.LABEL'),
      );
      return actions;
    }),
  );
}
